/**
 * useABRStats.ts — хук сбора расширенных ABR-метрик в реальном времени.
 *
 * Собирает метрики с умеренным интервалом:
 *   - currentQuality  — название активного уровня (360p/480p/720p)
 *   - bandwidth       — EWMA-оценка пропускной способности (kbps)
 *   - instantBitrate  — битрейт текущего HLS-уровня из манифеста (kbps)
 *   - bufferLength    — длина буфера впереди текущей позиции (сек)
 *   - bufferFillPercent — заполненность буфера в %
 *   - segmentLatency  — время загрузки последнего сегмента (мс)
 *   - switchCount     — количество переключений качества
 *
 * Слушает события:
 *   - LEVEL_SWITCHED  → пишет в лог переключений
 *   - FRAG_LOADED     → обновляет задержку сегмента
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import Hls, {
  type Events,
  type LevelSwitchedData,
  type FragLoadedData,
} from 'hls.js';
import type { ABRStats, QualitySwitchEvent, BandwidthDataPoint } from '../types';

/** Максимальное количество точек в истории */
const MAX_HISTORY_POINTS = 60;
const MAX_SWITCH_LOG_EVENTS = 60;
const STATS_POLL_INTERVAL_MS = 2500;

/** Максимальная длина буфера hls.js по умолчанию (сек) — для расчёта % заполнения */
const MAX_BUFFER_LENGTH = 30;

/** Карта высот → имена профилей */
const QUALITY_NAMES: Record<number, string> = {
  360:  '360p',
  480:  '480p',
  720:  '720p',
  1080: '1080p',
  1440: '2K',
  2160: '4K',
};

function getLevelName(hls: Hls, levelIndex: number): string {
  if (levelIndex < 0 || levelIndex >= hls.levels.length) return 'авто';
  const level = hls.levels[levelIndex];
  return QUALITY_NAMES[level.height] ?? `${level.height}p`;
}

/** Определить порядковый номер уровня по имени (для сравнения up/down) */
function getLevelOrder(name: string): number {
  const map: Record<string, number> = {
    '360p': 0, '480p': 1, '720p': 2,
    '1080p': 3, '2K': 4, '4K': 5,
  };
  return map[name] ?? 0;
}

interface UseABRStatsResult {
  stats: ABRStats;
  switchLog: QualitySwitchEvent[];
  bandwidthHistory: BandwidthDataPoint[];
}

export function useABRStats(
  hls: Hls | null,
  videoRef: React.RefObject<HTMLVideoElement>
): UseABRStatsResult {
  const [stats, setStats] = useState<ABRStats>({
    currentQuality: '—',
    bandwidth: 0,
    instantBitrate: 0,
    bufferLength: 0,
    bufferFillPercent: 0,
    segmentLatency: 0,
    switchCount: 0,
    timestamp: Date.now(),
  });

  const [switchLog, setSwitchLog] = useState<QualitySwitchEvent[]>([]);
  const [bandwidthHistory, setBandwidthHistory] = useState<BandwidthDataPoint[]>([]);

  const historyIndexRef = useRef(0);
  const prevLevelRef = useRef<number>(-1);
  // Последняя задержка сегмента — обновляется событием FRAG_LOADED
  const segmentLatencyRef = useRef<number>(0);
  // Счётчик переключений — ref чтобы не вызывать ре-рендер отдельно
  const switchCountRef = useRef<number>(0);

  // ─── Сбор метрик с умеренным интервалом ───────────────────────────────────

  const collectStats = useCallback(() => {
    if (!hls || !videoRef.current) return;

    const video = videoRef.current;

    // EWMA-оценка bandwidth из hls.js (bps → kbps)
    const bandwidthKbps = Math.round((hls.bandwidthEstimate ?? 0) / 1000);

    // Битрейт текущего уровня из манифеста (bps → kbps)
    // hls.currentLevel == -1 в авто-режиме → берём hls.loadLevel (реальный загружаемый уровень)
    const displayLevel = hls.currentLevel >= 0 ? hls.currentLevel : hls.loadLevel;
    const currentLevelObj =
      displayLevel >= 0 && displayLevel < hls.levels.length
        ? hls.levels[displayLevel]
        : null;
    const instantBitrateKbps = currentLevelObj
      ? Math.round(currentLevelObj.bitrate / 1000)
      : 0;

    // Длина буфера: конец ближайшего буферизованного диапазона − currentTime
    let bufferLen = 0;
    if (video.buffered.length > 0) {
      const ct = video.currentTime;
      for (let i = 0; i < video.buffered.length; i++) {
        if (video.buffered.start(i) <= ct && video.buffered.end(i) >= ct) {
          bufferLen = Math.max(0, video.buffered.end(i) - ct);
          break;
        }
      }
    }

    const bufferFill = Math.min(
      Math.round((bufferLen / MAX_BUFFER_LENGTH) * 100),
      100
    );

    const qualityName = getLevelName(hls, displayLevel);

    const newStats: ABRStats = {
      currentQuality: qualityName,
      bandwidth: bandwidthKbps,
      instantBitrate: instantBitrateKbps,
      bufferLength: Math.round(bufferLen * 10) / 10,
      bufferFillPercent: bufferFill,
      segmentLatency: segmentLatencyRef.current,
      switchCount: switchCountRef.current,
      timestamp: Date.now(),
    };

    setStats(newStats);

    // Добавляем точку в историю (последние MAX_HISTORY_POINTS)
    setBandwidthHistory((prev) => {
      const point: BandwidthDataPoint = {
        index: historyIndexRef.current++,
        bandwidth: bandwidthKbps,
        instantBitrate: instantBitrateKbps,
        bufferLength: Math.round(bufferLen * 10) / 10,
        quality: qualityName,
      };
      const updated = [...prev, point];
      return updated.length > MAX_HISTORY_POINTS
        ? updated.slice(updated.length - MAX_HISTORY_POINTS)
        : updated;
    });
  }, [hls, videoRef]);

  // Опрос с умеренным интервалом, чтобы графики не дёргали плеер слишком часто.
  useEffect(() => {
    if (!hls) return;
    const id = setInterval(collectStats, STATS_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hls, collectStats]);

  // ─── Лог переключений качества ────────────────────────────────────────────

  useEffect(() => {
    if (!hls) return;

    const handleLevelSwitched = (
      _event: Events.LEVEL_SWITCHED,
      data: LevelSwitchedData
    ) => {
      const prev = prevLevelRef.current;
      const next = data.level;

      if (prev !== next && prev !== -1) {
        const fromName = getLevelName(hls, prev);
        const toName   = getLevelName(hls, next);
        const bw       = Math.round((hls.bandwidthEstimate ?? 0) / 1000);
        const direction: QualitySwitchEvent['direction'] =
          getLevelOrder(toName) > getLevelOrder(fromName) ? 'up' : 'down';

        switchCountRef.current += 1;

        setSwitchLog((log) => {
          const updated = [
            ...log,
            { time: Date.now(), from: fromName, to: toName, bandwidth: bw, direction },
          ];
          return updated.length > MAX_SWITCH_LOG_EVENTS
            ? updated.slice(updated.length - MAX_SWITCH_LOG_EVENTS)
            : updated;
        });
      }

      prevLevelRef.current = next;
    };

    hls.on(Hls.Events.LEVEL_SWITCHED, handleLevelSwitched);
    return () => hls.off(Hls.Events.LEVEL_SWITCHED, handleLevelSwitched);
  }, [hls]);

  // ─── Задержка сегмента (FRAG_LOADED) ─────────────────────────────────────

  useEffect(() => {
    if (!hls) return;

    const handleFragLoaded = (_event: Events.FRAG_LOADED, data: FragLoadedData) => {
      // stats.loading: { start, first, end } — в миллисекундах
      const { start, end } = data.frag.stats.loading;
      if (end > start) {
        segmentLatencyRef.current = Math.round(end - start);
      }
    };

    hls.on(Hls.Events.FRAG_LOADED, handleFragLoaded);
    return () => hls.off(Hls.Events.FRAG_LOADED, handleFragLoaded);
  }, [hls]);

  return { stats, switchLog, bandwidthHistory };
}
